import React, { useState, useEffect } from "react";
import Button from "../Button";
import { useDispatch, useSelector } from "react-redux";
import {
  getBatReport,
  getEocReport,
  getBudgetItems,
  getInspections,
  getBatHistory,
  getEocHistory,
  getReportById,
} from "../../redux";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import type { AppDispatch } from "../../redux/store";
import { CustomDataTable } from "../DataTable/CustomDataTable";
import { ColumnDef } from "@tanstack/react-table";
import downloadIcon from "../../assets/images/Common/downloadIcon.png";
import { ActionButton } from "../BudgetList/BudgetList.styles";
import {
  ReportsContainer,
  ReportsHeader,
  TableContainer,
  TableWrapper,
} from "./Report.styles";

export const Report: React.FC<{
  projectId?: string;
  companyId?: string;
}> = ({ projectId, companyId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loadingBat, setLoadingBat] = useState(false);
  const [loadingEoc, setLoadingEoc] = useState(false);
  const [loading, setLoading] = useState(false);

  const [BatButtonDisabled, setBatButtonDisabled] = useState(true);
  const [EocButtonDisabled, setEocButtonDisabled] = useState(true);
  const [batData, setBatData] = useState<any[]>([]);
  const [eocData, setEocData] = useState<any[]>([]);

  const fetchBatHistory = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const batHistory: any = await dispatch(
        getBatHistory({ companyId, projectId, forceRefresh })
      );
      setBatData(batHistory || []);
    } catch (err) {
      console.error("Failed to fetch BAT history:", err);
      setBatData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEocHistory = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const eocHistory: any = await dispatch(
        getEocHistory({ companyId, projectId, forceRefresh })
      );
      setEocData(eocHistory || []);
    } catch (err) {
      console.error("Failed to fetch EOC history:", err);
      setEocData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && projectId) {
      fetchBatHistory();
      fetchEocHistory();
    }
  }, [companyId, projectId, dispatch]);

  useEffect(() => {
    dispatch(
      getBudgetItems({ pId: projectId, cId: companyId }, (budgetItems) => {
        if (budgetItems.length > 0) {
          setBatButtonDisabled(false);
        } else {
          setBatButtonDisabled(true);
        }
      })
    );
  }, [projectId, companyId, dispatch]);

  useEffect(() => {
    dispatch(
      getInspections({ pId: projectId, cId: companyId }, (inspectionItems) => {
        if (inspectionItems?.length > 0) {
          setEocButtonDisabled(false);
        } else {
          setEocButtonDisabled(true);
        }
      })
    );
  }, [projectId, companyId, dispatch]);

  const notifyError = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
    });
  };

  const handleBatReport = async () => {
    if (companyId && projectId) {
      setLoadingBat(true);
      try {
        const { payload } = (await dispatch(
          getBatReport({ companyId, projectId })
        )) as any;
        if (
          payload &&
          typeof payload === "object" &&
          "base64" in payload &&
          payload.base64
        ) {
          downloadPDF(payload);
          await fetchBatHistory(true);
        } else {
          console.error("Report generation failed: No PDF data received.");
        }
      } catch (error) {
        notifyError("Failed to generate Report");
        console.error("Error generating BAT report:", error.message);
      } finally {
        setLoadingBat(false);
      }
    }
  };

  const handleEocReport = async () => {
    if (companyId && projectId) {
      setLoadingEoc(true);
      try {
        const { payload } = (await dispatch(
          getEocReport({ companyId, projectId })
        )) as any;
        if (
          payload &&
          typeof payload === "object" &&
          "base64" in payload &&
          payload.base64
        ) {
          downloadPDF(payload);
          await fetchEocHistory(true);
        } else {
          console.error("Report generation failed: No PDF data received.");
        }
      } catch (error) {
        notifyError("Failed to generate Report");
        console.error("Error generating EOC report:", error.message);
      } finally {
        setLoadingEoc(false);
      }
    }
  };

  const downloadPDF = (payload) => {
    const { base64, fileName, mimeType } = payload;
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length)
      .fill(0)
      .map((_, i) => byteCharacters.charCodeAt(i));
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType || "application/pdf" });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName || "report.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const batTooltip = BatButtonDisabled
    ? "Budget report is not available due to missing budget records, budget amounts"
    : "Generate Budget Analysis report as a PDF file";
  const eocTooltip = EocButtonDisabled
    ? "TimeLine report is not available due to missing inspection records"
    : "Generate Timeline Report as a PDF file";

  const budgetColumns: ColumnDef<any>[] = [
    {
      accessorKey: "lastModified",
      header: "Date",
      enableSorting: true,
    },
    {
      id: "actions",
      header: "Action",
      cell: ({ row }) => actionBodyTemplate(row.original),
      enableSorting: false,
    },
  ];
  const inspectionColumns: ColumnDef<any>[] = [
    {
      accessorKey: "lastModified",
      header: "Date",
      enableSorting: true,
    },
    {
      id: "actions",
      header: "Action",
      cell: ({ row }) => actionBodyTemplate(row.original),
      enableSorting: false,
    },
  ];
  const handleDownload = async (rowData) => {
    try {
      const signedUrl = await dispatch(
        getReportById({ companyId, projectId, fileId: rowData.fileName })
      );

      if (!signedUrl) {
        console.error("No signed URL returned");
        return;
      }

      const link = document.createElement("a");
      link.href = signedUrl;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // window.open(signedUrl, "_blank", "noopener,noreferrer");
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <ActionButton onClick={() => handleDownload(rowData)}>
        <img
          src={downloadIcon}
          alt="downloadIcon"
          style={{
            width: 16,
            height: 16,
            objectFit: "contain",
            cursor: "pointer",
          }}
        />
      </ActionButton>
    );
  };

  return (
    <ReportsContainer>
      <ReportsHeader>
        <span>Generate New Reports</span>
      </ReportsHeader>
      <div className="flex gap-2 mb-1">
        <div title={batTooltip}>
          <Button
            variant="primary"
            size="small"
            onClick={handleBatReport}
            disabled={
              BatButtonDisabled || loadingBat || !companyId || !projectId
            }
          >
            {loadingBat ? "Generating…" : "Budget PDF"}
          </Button>
        </div>
        <div title={eocTooltip}>
          <Button
            variant="secondary"
            size="small"
            onClick={handleEocReport}
            disabled={
              EocButtonDisabled || loadingEoc || !companyId || !projectId
            }
          >
            {loadingEoc ? "Generating…" : "Timeline PDF"}
          </Button>
        </div>
      </div>
      <ReportsHeader>
        <span>Reports Hub</span>
      </ReportsHeader>
      <TableContainer>
        <TableWrapper>
          <ReportsHeader>
            <span>Budget Reports</span>
          </ReportsHeader>
          <CustomDataTable
            data={batData || []}
            columns={budgetColumns}
            loading={loading}
          />
        </TableWrapper>
        <TableWrapper>
          <ReportsHeader>
            <span>Timeline Reports</span>
          </ReportsHeader>
          <CustomDataTable
            data={eocData || []}
            columns={inspectionColumns}
            loading={loading}
          />
        </TableWrapper>
      </TableContainer>
    </ReportsContainer>
  );
};

export default Report;
